import logger from './logger';

export type FetchHeader = {
    key: string;
    value: string;
};

export const buildSearchParams = (params: Map<string, string> | object | undefined): URLSearchParams | undefined => {

    if (!params) return undefined;

    const searchParams = new URLSearchParams();

    if (params instanceof Map) {
        params.forEach((value, key) => {
            searchParams.append(key, value);
        });
    } else {
        Object.entries(params).forEach(([key, value]) => {
            searchParams.append(key, value);
        });
    }
    
    return searchParams;
};

const prepareRoute = (serviceUrl: string, path: string, searchParams?: URLSearchParams) => {

    let finalRoute = '';

    if (serviceUrl.endsWith('/') && path.startsWith('/')) {
        finalRoute = serviceUrl.slice(0, -1);
    }else if (!serviceUrl.endsWith('/') && !path.startsWith('/')) {
        finalRoute = `${serviceUrl}/`;
    } else {
        finalRoute = serviceUrl;
    }   

    finalRoute += path;

    if (searchParams) {
        const separator = path.includes('?') ? '&' : '?';
        finalRoute = `${finalRoute}${separator}${searchParams.toString()}`;
    }

    return finalRoute;
}

const dataFetcher = (serviceUrl: string, apiKey?: string, authToken?: string) => {

    const headers: Record<string, string> = {};

    if (apiKey) {
        headers['x-api-key'] = apiKey;
    }

    if (authToken) {   
        headers['Authorization'] = `Bearer ${authToken}`;
    }

    const restHeader = {...headers};
    const multipartHeader = {...headers};

    multipartHeader['Content-Type'] = 'multipart/form-data';
 
    restHeader['Accept'] = 'application/json';
    restHeader['Content-Type'] = 'application/json';

    return {
        get: async (path: string, searchParams?: URLSearchParams) => {
            const route = prepareRoute(serviceUrl, path, searchParams);
            logger.info(`Fetching data from ${route}`);

            return await fetch(route, {
                headers: restHeader,
                method: 'GET'
            });
        },

        post: async (path: string, body: object | FormData, isMultipart?: boolean) => {
            const route = prepareRoute(serviceUrl, path);
            logger.info(`Posting data to ${route}`);

            return await fetch(route, {
                headers: isMultipart ? multipartHeader : restHeader,
                method: 'POST',
                body: isMultipart ? body as FormData : JSON.stringify(body),
            });
        },

        put: async (path: string, body: object) => {
            const route = prepareRoute(serviceUrl, path);
            logger.info(`Putting data to ${route}`);

            return await fetch(route, {
                headers: restHeader,
                method: 'PUT',
                body: JSON.stringify(body),
            });
        },

        patch: async (path: string, body: object) => {
            const route = prepareRoute(serviceUrl, path);
            logger.info(`Patching data to ${route}`);

            return await fetch(route, {
                headers: restHeader,
                method: 'PATCH',
                body: JSON.stringify(body),
            });
        },

        delete: async (path: string, body?: object, searchParams?: URLSearchParams) => {
            const route = prepareRoute(serviceUrl, path, searchParams);
            logger.info(`Deleting data from ${route}`);

            return await fetch(route, {
                headers: restHeader,
                method: 'DELETE',
                body: body ? JSON.stringify(body) : undefined,
            });
        },
    }
};

export default dataFetcher;
